/**
 * Formats a decimal hours value as a friendly Dutch duration:
 *   0     → "0u"
 *   0.25  → "0u 15m"
 *   0.5   → "0u 30m"
 *   1     → "1u"
 *   1.25  → "1u 15m"
 *   2.5   → "2u 30m"
 *
 * Negative values clamp to 0. Sub-minute precision is rounded to the
 * nearest minute (so 0.0083 hours = 0u 0m, not "0u 1m").
 */
export function formatHours(decimal: number | null | undefined): string {
  const v = Math.max(0, Number(decimal) || 0);
  const totalMinutes = Math.round(v * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (m === 0) return `${h}u`;
  return `${h}u ${m}m`;
}

/** "+0.5 punten" / "1 punt" / "0.25 punten". Strips trailing zeros. */
export function formatPoints(decimal: number | null | undefined): string {
  const v = Math.max(0, Number(decimal) || 0);
  const rounded = Math.round(v * 100) / 100;
  const label = rounded === 1 ? 'punt' : 'punten';
  const num = Number.isInteger(rounded) ? String(rounded) : rounded.toString();
  return `${num} ${label}`;
}

/**
 * Parse a user-entered decimal, accepting both Dutch ("4,5") and English
 * ("4.5") notation. Returns null on empty or unparseable input.
 *
 * Why: HTML `type="number"` inputs reject the comma form silently in NL
 * locale, so we model decimals as strings and normalize here at submit.
 */
export function parseDecimal(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null;
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;
  const trimmed = input.trim().replace(',', '.');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}
